import { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { useGetLocationsQuery } from '../../store/api/sensors';
import {
  resetSecondarySensor,
  selectAddingSeason,
  selectMainSensorPeriod,
  selectNextSensor,
  selectPrevSensor,
  selectScrolledTimeline,
  selectSecondarySensor,
  updateSorting,
} from '../../store/slices/sensors';
import { isDateInPeriod } from '../../utils/dates';
import { CropsSelect } from '../CropsSelect/CropsSelect.jsx';
import {
  Sidebar,
  SidebarFilter,
  SidebarItemButton,
  SidebarList,
  SidebarListExpanded,
  SidebarField,
} from '../Sidebar/index.jsx';
import { BreakpointDesktop, PathnamesMap } from '../../constants/ui';
import { useViewport } from '../../hooks';
import { MapLat, MapLng } from '../../constants/keys';

const SensorSidebar = memo(({ t, seasons }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [unboundSensors, setUnboundSensors] = useState([]);
  const [seasonsList, setSeasonsList] = useState([]);
  const [cropsList, setCropsList] = useState([]);
  const [params, setParams] = useSearchParams();
  const { pathname } = useLocation();
  const { widthViewport } = useViewport();
  const noNameLabel = t('empty.noName');

  const {
    selectedSensor,
    selectedSecondarySensor,
    selectedDate,
    sorting: { dir, parameter, crop },
  } = useSelector((state) => state.sensors);
  const { crops } = useSelector((state) => state.catalog);

  const { data: allSensors = [], isSuccess: isAllSensorSuccess } = useGetLocationsQuery();

  const onChangeMainSensor = ({ id }) => {
    if (id !== selectedSecondarySensor && id !== selectedSensor) {
      let path = '';
      if (pathname.includes(PathnamesMap.timeline)) {
        path = PathnamesMap.timeline;
      } else if (pathname.includes(PathnamesMap.charts)) {
        path = PathnamesMap.charts;
      } else if (pathname.includes(PathnamesMap.vegetation)) {
        path = PathnamesMap.vegetation;
      } else if (pathname.includes(PathnamesMap.photo)) {
        path = PathnamesMap.photo;
      }
      navigate({
        pathname: `/${path}/${id}`,
        search: `?date=${selectedDate}`,
      });
      dispatch(selectScrolledTimeline(false));
    }
  };

  const onChangeCompareSensor = ({ id }) => {
    if (id !== selectedSecondarySensor) {
      dispatch(selectSecondarySensor({ id }));
    } else {
      dispatch(resetSecondarySensor());
    }
  };

  const onAddSeason = (location) => {
    const coordinates =
      location.sensor && location.sensor.gps_point
        ? location.sensor.gps_point.coordinates
        : location.gps_point
          ? location.gps_point.coordinates
          : location.install_point.coordinates;
    localStorage.setItem(MapLng, coordinates[0]);
    localStorage.setItem(MapLat, coordinates[1]);

    if (location.installDate && location.removalDate) {
      dispatch(selectMainSensorPeriod({ period: [location.installDate, location.removalDate] }));
    }
    dispatch(selectAddingSeason(true));
    navigate(`/${PathnamesMap.fields}`);
  };

  const onChangeFilterCrop = (value) => {
    if (value.id === 'all') {
      params.delete('tab');
    } else {
      params.set('tab', value.id);
    }
    setParams(params);
    dispatch(updateSorting({ dir, parameter, crop: value.id }));
  };

  useEffect(() => {
    if (!isAllSensorSuccess || !crop) return;

    const cropsCurrent = [];
    const list = [];
    const unboundSensorsList = [];

    allSensors.map((obj) => {
      if (
        !obj.field_season_id &&
        (isDateInPeriod(obj.install_date, obj.removal_date, selectedDate) ||
          obj.removal_date === selectedDate ||
          obj.install_date === selectedDate)
      ) {
        unboundSensorsList.push({
          id: obj.id,
          name: obj.name,
          isSoilSensor: !!(
            obj.sensor.settings && obj.sensor.settings.enabled.find((s) => s.name === 'pair_soil_sensor')
          ),
          isAiEnabled:
            obj.sensor.settings.enabled &&
            obj.sensor.settings.enabled.find((item) => item.name === 'ai_photo_analysis'),
          removalDate: obj.removal_date,
          installDate: obj.install_date,
          gps_point: obj.gps_point,
          install_point: obj.install_point,
          sensor: {
            gps_point: obj.sensor.gps_point,
          }
        });
      }

      if (seasons && seasons.length) {
        const seasonsFiltered = seasons.filter((s) => s.id === obj.field_season_id);
        const season = seasonsFiltered && seasonsFiltered.length > 0 ? seasonsFiltered[0] : null;
        if (season) {
          // filtered sensors by crop
          if (crop === 'all' || season.crop_id === crop) {
            const isFieldExist = list.find((f) => f.id === season.id);
            if (!isFieldExist) {
              const seasonSensor = [
                {
                  ...obj,
                  isSoilSensor: !!(
                    obj.sensor.settings && obj.sensor.settings.enabled.find((s) => s.name === 'pair_soil_sensor')
                  ),
                },
              ];
              const filteredSeasonSensors = seasonSensor.filter(
                (s) =>
                  isDateInPeriod(s.install_date, s.removal_date, selectedDate) ||
                  s.removal_date === selectedDate ||
                  s.install_date === selectedDate,
              );
              if (filteredSeasonSensors && filteredSeasonSensors.length > 0) {
                list.push({
                  id: season.id,
                  field: season.field,
                  harvestDate: season.harvest_date,
                  sowingDate: season.sowing_date,
                  crop: season.crop_id,
                  sensors: seasonSensor,
                });
              }
            } else {
              list.map((s) => {
                if (s.id === season.id) {
                  s.sensors.push({
                    ...obj,
                    isSoilSensor:
                      obj.sensor.settings && obj.sensor.settings.enabled.find((s) => s.name === 'pair_soil_sensor'),
                  });
                }
              });
            }
          }

          // workspace crops
          if (!cropsCurrent.find((item) => item.id === season.crop_id)) {
            cropsCurrent.push({
              id: season.crop_id,
              color: crops.filter((item) => item.id === season.crop_id)[0]?.color || 'transparent',
            });
          }
        }
      }
    });

    // list sensors for swiper in timeline page
    const sensors = [];
    if (list.length > 0) {
      list.map((s) => {
        if (s.sensors && s.sensors.length > 0) {
          s.sensors.map((item) => {
            // if (!dayjs(item.sensor.synced_at).isBefore(selectedDate) &&
            //   isDateInPeriod(item.install_date, item.removal_date, selectedDate)) {
            if (
              !dayjs(item.sensor.synced_at).isBefore(selectedDate) &&
              (isDateInPeriod(item.install_date, item.removal_date, selectedDate) ||
                dayjs(item.sensor.synced_at) === dayjs(selectedDate) ||
                item.removal_date === selectedDate ||
                item.install_date === selectedDate)
            ) {
              sensors.push({
                id: item.id,
                name: item.sensor.name,
                installDate: item.install_date,
                removalDate: item.removal_date,
              });
            }
          });
        }
      });
    }
    if (unboundSensorsList.length > 0) {
      unboundSensorsList.map((s) => {
        sensors.push({
          id: s.id,
          name: s.name,
        });
      });
    }

    if (sensors.length > 1) {
      sensors.map((s, index) => {
        if (s.id === selectedSensor) {
          dispatch(selectNextSensor(sensors[index + 1] ? sensors[index + 1] : sensors[0]));
          if (sensors.length > 2) {
            dispatch(selectPrevSensor(sensors[index - 1] ? sensors[index - 1] : sensors[sensors.length - 1]));
          }
        }
      });
    }

    setCropsList(cropsCurrent);
    setSeasonsList(list);
    setUnboundSensors(unboundSensorsList);
  }, [isAllSensorSuccess, crop, seasons]);

  return (
    widthViewport > BreakpointDesktop && (
      <Sidebar hideOnTablet={true}>
        {/*<div className={styles.sensorSidebarSearch}>
        <Input
          size="large"
          prefix={<SearchOutlined style={{ color: '#ADB5BD' }} />}
          allowClear
          placeholder="Search by name"
          onChange={() => {}}
          className={styles.sensorSidebarSearchInput}
        />
        <button className={styles.sensorSidebarSort} onClick={() => {}}>
          <SortAscendingOutlined style={{ color: '#ADB5BD' }} />
        </button>
      </div>*/}
        <SidebarFilter>
          {cropsList && cropsList.length > 0 && (
            <CropsSelect
              t={t}
              cropsList={cropsList}
              currentCrop={crop}
              isFilter={true}
              changeCrop={onChangeFilterCrop}
            />
          )}
        </SidebarFilter>
        <SidebarList>
          {seasonsList.map((season) => (
            <SidebarField
              key={season.id}
              t={t}
              title={season.field}
              crop={season.crop}
              startDate={season.sowingDate}
              endDate={season.harvestDate}
            >
              {season.sensors.length > 0
                ? season.sensors.map((item) => (
                    <SidebarItemButton
                      key={item.id}
                      title={item.name || noNameLabel}
                      isActive={selectedSensor === item.id}
                      isDisabled={
                        !dayjs(item.sensor.synced_at).isBefore(selectedDate) &&
                        !isDateInPeriod(item.install_date, item.removal_date, selectedDate)
                      }
                      isSoilSensor={item.isSoilSensor}
                      isAiEnabled={
                        item.sensor.settings.enabled &&
                        item.sensor.settings.enabled.find((item) => item.name === 'ai_photo_analysis')
                      }
                      showCompareButton={selectedSensor !== item.id}
                      isCompareButtonActive={selectedSecondarySensor === item.id}
                      onClickButton={() => onChangeMainSensor({ id: item.id })}
                      onClickCompareButton={() => onChangeCompareSensor({ id: item.id })}
                      t={t}
                    />
                  ))
                : ''}
            </SidebarField>
          ))}

          {/*Unbound Sensors*/}
          {unboundSensors.length > 0 && (
            <SidebarListExpanded t={t}>
              {unboundSensors.map((item) => (
                <SidebarItemButton
                  key={item.id}
                  title={item.name || noNameLabel}
                  isActive={selectedSensor === item.id}
                  isAiEnabled={item.isAiEnabled}
                  isSoilSensor={item.isSoilSensor}
                  showCompareButton={selectedSensor !== item.id}
                  showAddSeasonButton={true}
                  isCompareButtonActive={selectedSecondarySensor === item.id}
                  onClickButton={() => onChangeMainSensor({ id: item.id })}
                  onClickCompareButton={() => onChangeCompareSensor({ id: item.id })}
                  onClickAddSeason={() => onAddSeason(item)}
                  t={t}
                />
              ))}
            </SidebarListExpanded>
          )}
        </SidebarList>
      </Sidebar>
    )
  );
});

SensorSidebar.displayName = 'SensorSidebar';

export default SensorSidebar;
